import { Button, FAIcon } from '@/components_deprecated'
import { Module } from '@/modules/companies/consts'
import { SalaryType } from '@/modules/companies/types.graphql'
import { USER_SHALLOW_QUERY } from '@/modules/users/queries'
import { useBreadcrumbs, useModuleConfig, useParam } from '@/utils/hooks'
import { getFullMinutes } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { format, setSeconds } from 'date-fns'
import { Fragment, useEffect, useEffectEvent, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import { useSalaryModuleTranslations } from '../../hooks'
import { useSalaryMutations } from '../../util'
import type { SalarySettingsForm } from './SalarySettingsFormView'
import { SalarySettingsFormView } from './SalarySettingsFormView'
import { SelectSalaryModule } from './SelectSalaryModule'
import { H2 } from '@/components'

const Create = styled.div`
  position: relative;
`

const FlexHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

interface CreateSalaryWorkTimeProps {}

export const CreateSalaryWorkTime: React.FC<CreateSalaryWorkTimeProps> = () => {
  const userId = useParam('userId', true)

  const location = useLocation()

  const initialSalaryModule =
    (location.state as { salaryModule: Module } | null)?.salaryModule ?? null

  const [selectSalaryModuleOpen, setSelectSalaryModuleOpen] = useState(false)
  const getModuleTranslation = useSalaryModuleTranslations()

  const [configName, setConfigName] = useState<Module | null>(
    initialSalaryModule
  )
  const { moduleConfig } = useModuleConfig(configName ?? Module.ACTIVITIES)
  const [initialValidation, setInitialValidation] = useState(false)
  const [breadcrumbsOverridden, setBreadcrumbsOverridden] = useState(false)
  const { setOverride } = useBreadcrumbs()

  const translations = useTranslate({
    createSalarySettings: ['settings.create-salary-settings', { module: '' }],

    selectModule: 'settings.select-module',

    validation: {
      driverPause: 'settings.validation.salary-driver-pause-length',
      invalid: 'common.invalid',
      required: 'common.required',
      priority: ['settings.validation.salary-priority-unique', { module: '' }],
    },
  })

  const { data: userData } = useQuery(USER_SHALLOW_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { id: userId ?? '' },
  })

  const mutations = useSalaryMutations()

  const secondsToDate = (seconds: number) =>
    setSeconds(new Date(1993, 0, 9), seconds)

  const form = useForm<SalarySettingsForm>({
    values: {
      salaryGroups: [],
      userId: userId ?? '',
      salaryType: SalaryType.HOUR,

      defaultSalaryCode: null,
      defaultSalaryCodeWhenOvertime: null,
      drivingSalaryCode: null,
      otherWorkSalaryCode: null,
      availabilitySalaryCode: null,
      salaryDayCode: null,

      dayOvertimeSalaryCode: null,
      weekOvertimeSalaryCode: null,
      monthOvertimeSalaryCode: null,

      useDayOvertime: true,
      useWeekOvertime: true,
      useMonthOvertime: false,

      calculateWeekOvertimeInline: false,
      calculateOvertimeAsAddon: false,

      dailyWorkLimit: 8,
      periodWorkLimit: 40,
      pauseDuration: secondsToDate(2700),
      pauseThreshold: secondsToDate(14400),
      subtractPaidPauseAfterTotalWorkThreshold: false,
      countPaidPauseInTotalWork: false,
      paidPauseThreshold: secondsToDate(0),
      salaryDateStart: 26,
      salaryDateEnd: 25,
      name: '',
      groupSalaryOnVehicle: false,
      periodAddons: [],
      periodOvertimes: [],
      useSalaryDayPayment: false,
      defaultDepartment: null,
      availableDepartments: [],
      useAutomaticDietGeneration: false,
      useAutomaticSalaryDayGeneration: false,
      paidPauseSalaryCode: null,
      availableAddons: [],
      holidayAllowances: [],
    },
    validators: {
      salaryDateStart: (val, { salaryDateEnd }) =>
        val === 1 && salaryDateEnd === 31
          ? null
          : val > salaryDateEnd
          ? null
          : translations.validation.invalid,
    },
    config: {
      validateImmediately: true,
      initAsInvalid: true,
    },
  })

  async function handleCreate() {
    if (moduleConfig === null) return

    const { formValues: data } = form
    void mutations.create({
      variables: {
        input: {
          moduleConfig: moduleConfig.id,
          user: userId ?? '',
          salaryGroups: data.salaryGroups,
          dailyWorkLimit: data.dailyWorkLimit,
          periodWorkLimit: data.periodWorkLimit,
          pauseDuration: getFullMinutes(data.pauseDuration),
          pauseThreshold: getFullMinutes(data.pauseThreshold),
          subtractPaidPauseAfterTotalWorkThreshold:
            data.subtractPaidPauseAfterTotalWorkThreshold,
          countPaidPauseInTotalWork: data.countPaidPauseInTotalWork,
          paidPauseThreshold: getFullMinutes(data.paidPauseThreshold),
          paidPauseSalaryCode: form.formValues.paidPauseSalaryCode,
          salaryDateStart: data.salaryDateStart,
          salaryDateEnd: data.salaryDateEnd,
          name: data.name,
          groupSalaryOnVehicle: data.groupSalaryOnVehicle,
          useSalaryDayPayment: data.useSalaryDayPayment,
          salaryType: data.salaryType,
          defaultSalaryCode: data.defaultSalaryCode,
          defaultSalaryCodeWhenOvertime: data.defaultSalaryCodeWhenOvertime,
          drivingSalaryCode: form.formValues.drivingSalaryCode,
          otherWorkSalaryCode: form.formValues.otherWorkSalaryCode,
          availabilitySalaryCode: form.formValues.availabilitySalaryCode,
          salaryDayCode: data.salaryDayCode,
          dayOvertimeSalaryCode: data.dayOvertimeSalaryCode,
          useWeekOvertime: data.useWeekOvertime,
          useDayOvertime: data.useDayOvertime,
          addOvertimeAsAddon: data.calculateOvertimeAsAddon,
          weekOvertimeSalaryCode: data.weekOvertimeSalaryCode,
          calculateWeekOvertimeInline: data.calculateWeekOvertimeInline,
          useMonthOvertime: data.useMonthOvertime,
          monthOvertimeSalaryCode: data.monthOvertimeSalaryCode,
          useAutomaticDietGeneration: data.useAutomaticDietGeneration,
          useAutomaticSalaryDayGeneration: data.useAutomaticSalaryDayGeneration,
          periodAddonsManual: data.periodAddons.map(periodAddon => {
            const {
              id,
              salaryCodeName,
              alwaysActive,
              ...periodAddonNodeInput
            } = periodAddon
            return {
              ...periodAddonNodeInput,
              start: format(periodAddon.start, 'HH:mm'),
              end: format(periodAddon.end, 'HH:mm'),
            }
          }),
          periodOvertimesManual: data.periodOvertimes.map(periodOvertime => {
            const {
              id,
              salaryCodeName,
              override,
              excludeIfOvertime,
              ...periodOvertimeNodeInput
            } = periodOvertime
            return {
              ...periodOvertimeNodeInput,
              start: format(periodOvertime.start, 'HH:mm'),
              end: format(periodOvertime.end, 'HH:mm'),
            }
          }),
          defaultDepartment: data.defaultDepartment,
          availableDepartments: data.availableDepartments,
          selectableSalaryCodes: data.availableAddons,
          holidayAllowances: data.holidayAllowances,
        },
      },
    })
  }

  async function updateSalaryModule(module: Module) {
    setConfigName(module)
    setSelectSalaryModuleOpen(false)
  }

  const title = moduleConfig
    ? translations.createSalarySettings({
        module: getModuleTranslation(moduleConfig.name),
      })
    : translations.createSalarySettings({ module: '' })

  const handleInitialValidation = useEffectEvent(() => {
    form.validateForm()
    setInitialValidation(true)
  })

  useEffect(() => {
    if (moduleConfig === null || initialValidation) return
    handleInitialValidation()
  }, [initialValidation, moduleConfig])

  const handleBreadcrumbOverrides = useEffectEvent(() => {
    if (!userData?.user) return
    setOverride(userData.user.id, userData.user.fullName)
    setBreadcrumbsOverridden(true)
  })

  useEffect(() => {
    if (!breadcrumbsOverridden) handleBreadcrumbOverrides()
  }, [breadcrumbsOverridden])

  const UserWrapper = userId ? Wrapper : Fragment

  return (
    <UserWrapper>
      <FlexHeader>
        <H2>{title}</H2>
        {!userId && (
          <Create>
            {isMobileOnly ? (
              <Button
                height="32px"
                padding="0 1rem"
                onClick={() => setSelectSalaryModuleOpen(true)}
              >
                <FAIcon icon="plus" $size="0.8rem" />
              </Button>
            ) : (
              <Button
                height="48px"
                onClick={() => setSelectSalaryModuleOpen(true)}
              >
                {translations.selectModule}
              </Button>
            )}
            <SelectSalaryModule
              open={selectSalaryModuleOpen}
              onClose={() => setSelectSalaryModuleOpen(false)}
              onSubmit={salaryModule => updateSalaryModule(salaryModule)}
            />
          </Create>
        )}
      </FlexHeader>
      {moduleConfig && (
        <SalarySettingsFormView
          form={form}
          config={moduleConfig}
          onSubmit={handleCreate}
        />
      )}
    </UserWrapper>
  )
}
